import React, { useCallback } from "react"
import Button from "@mui/material/Button"
import ChevronLeft from "@mui/icons-material/ChevronLeft"
import ChevronRight from "@mui/icons-material/ChevronRight"
import { grey } from "@mui/material/colors"
import CalendarPopover from "./popover"
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar"
import store from "@/store"
import i18n from "@/i18n"
import calendarIcon from "./calendar_icon.svg"

// Minimum widths (px) for the date button per locale, estimated from the
// longest possible formatted date string in each language:
//   en: "Wednesday, 28 September 2024" (28 chars)
//   pl: "poniedziałek, 28 października 2024" (34 chars)
const MIN_WIDTH_BY_LOCALE: Record<string, number> = {
  en: 256,
  pl: 290,
}

const DEFAULT_MIN_WIDTH = 120

const CalendarPicker: React.FC = () => {
  const { currentDate, setCurrentDate } = store

  const onPrevDay = () => setCurrentDate(currentDate.minus({ days: 1 }))
  const onNextDay = () => setCurrentDate(currentDate.plus({ days: 1 }))

  const weekday = currentDate.toFormat(i18n.t("app.date.formats.weekday"))
  const restOfDate = currentDate.toFormat(i18n.t("app.date.formats.nav_date"))
  const minWidth = MIN_WIDTH_BY_LOCALE[i18n.locale] ?? DEFAULT_MIN_WIDTH

  const buttonElement = useCallback(
    ({ onClick }: { onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void }) => (
      <button
        type="button"
        className="flex items-center gap-2.5 h-8 px-5 text-nowrap bg-white border-y border-solid border-[#eee]"
        style={{ minWidth }}
        onClick={onClick}
        data-testid="calendar-picker-date"
      >
        <img src={calendarIcon} alt="" className="w-3 h-3.5" />
        <span className="text-sm leading-5">
          <span className="font-medium text-neutral-900">{weekday}</span>
          <span className="text-neutral-900">, {restOfDate}</span>
        </span>
      </button>
    ),
    [weekday, restOfDate, minWidth],
  )

  return (
    <div className="flex items-center" data-testid="calendar-picker-container">
      <Button
        variant="ui-element"
        className="w-[30px] min-w-0 h-8 !border-[#eee]"
        color="inherit"
        onClick={onPrevDay}
        data-testid="calendar-picker-prev"
      >
        <ChevronLeft fontSize="small" htmlColor={grey[600]} />
      </Button>

      <CalendarPopover buttonElement={buttonElement}>
        <div style={{ fontSize: 16 }}>
          <DateCalendar value={currentDate} onChange={setCurrentDate} />
        </div>
      </CalendarPopover>

      <Button
        variant="ui-element"
        className="w-[30px] min-w-0 h-8 !border-[#eee]"
        color="inherit"
        onClick={onNextDay}
        data-testid="calendar-picker-next"
      >
        <ChevronRight fontSize="small" htmlColor={grey[600]} />
      </Button>
    </div>
  )
}

export default CalendarPicker
